/**
 * frontend/src/pages/PublicLanding.jsx
 * SoundDAO — The public front door. Signed-out root URL.
 *
 * Sprint 6-Zero Milestone 3.
 *
 * Background: the value-prop copy below was written in May 2026 and SHIPPED —
 * the .vp-* rules are in the production CSS bundle today. It was just built
 * inside LandingPage.jsx (now PersonaOnboarding.jsx), the session-gated persona
 * picker at /onboarding. So the only people who ever saw the pitch were people
 * who had already signed up. This file lifts that content out to where a
 * stranger can read it.
 *
 * Deliberately dependency-free of auth: no session prop, no useAuth, no fetch.
 * It must render for someone who has never heard of SoundDAO.
 *
 * Reuses LandingPage.css so no new CSS ships — the classes are already live.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useRates, plus } from '../lib/rates';
import '../LandingPage.css';
import './PublicLanding.css';
import BrandMark from '../components/BrandMark';

export default function PublicLanding() {
  const rates = useRates();
  return (
    <div className="public-landing">

      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <header className="pl-nav">
        <div className="pl-nav-brand">
          <span className="pl-nav-logo"><BrandMark /></span>
          <span className="pl-nav-name">SoundDAO</span>
        </div>
        <nav className="pl-nav-links">
          <Link className="pl-nav-link" to="/creators">Discover artists</Link>
          <Link className="pl-nav-link" to="/leaderboard">Leaderboard</Link>
          <Link className="pl-nav-signin" to="/login">Sign in</Link>
        </nav>
      </header>

      <div className="vp-section">

        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <div className="vp-hero">
          <h2 className="vp-headline">
            Discover artists. Back them early.<br />
            Earn SOUND when they blow up.
          </h2>
          <p className="vp-sub">
            The music game where fans earn SOUND by discovering
            independent artists, artists build their machine
            from Raw to Wildfire, and tastemakers spot
            who&#39;s next.
          </p>
        </div>

        {/* ── Primary CTA — browse first, sign up second ─────────────────── */}
        {/* /creators and /leaderboard are already public (App.js PUBLIC_PATHS),
            so the funnel is the product itself, not a wall. */}
        <div className="pl-cta-row">
          <Link className="pl-cta-primary" to="/creators">
            Discover artists →
          </Link>
          <Link className="pl-cta-secondary" to="/login">
            Sign in or create an account
          </Link>
        </div>
        <p className="pl-cta-note">
          Browsing is free and needs no account. Free to join, always.
        </p>

        {/* ── Stat pills ─────────────────────────────────────────────────── */}
        <div className="vp-stats">
          <div className="vp-stat">
            <span className="vp-stat-num vp-orange">{plus(rates, 'recruit_creator')}</span>
            <span className="vp-stat-label">SOUND per artist recruited</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-purple">Raw→Wildfire</span>
            <span className="vp-stat-label">the creator arc</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-amber">Street team</span>
            <span className="vp-stat-label">fans who back you early</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-green">Free</span>
            <span className="vp-stat-label">to join, always</span>
          </div>
        </div>

        <div className="vp-divider" />

        {/* ── Column headers ─────────────────────────────────────────────── */}
        <div className="vp-col-headers">
          <span className="vp-col-label vp-col-fan">For fans</span>
          <span className="vp-col-label vp-col-creator">For artists</span>
          <span className="vp-col-label vp-col-collector">For collectors</span>
        </div>

        {/* ── Props grid ─────────────────────────────────────────────────── */}
        <div className="vp-grid">
          {/* Row 1 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">🎧</span>
            <div className="vp-card-title">Earn SOUND for discovering</div>
            <div className="vp-card-desc">Earn SOUND backing artists before everyone else does.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">🎤</span>
            <div className="vp-card-title">Build your machine</div>
            <div className="vp-card-desc">Post content, grow your street team, monetize your fanbase.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">💎</span>
            <div className="vp-card-title">Spot them first</div>
            <div className="vp-card-desc">Track rising artists by Heat Score before anyone else notices.</div>
          </div>

          {/* Row 2 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">🏆</span>
            <div className="vp-card-title">Climb Big Creative</div>
            <div className="vp-card-desc">Build your roster. The best scouts rise to the top.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">🔥</span>
            <div className="vp-card-title">Drop like a label</div>
            <div className="vp-card-desc">Schedule releases. Notify your street team. 3x SOUND for 24hrs.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">📈</span>
            <div className="vp-card-title">Follow the Heat</div>
            <div className="vp-card-desc">Heat Score is recalculated nightly. Watch who is actually moving.</div>
          </div>

          {/* Row 3 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">⚡</span>
            <div className="vp-card-title">Early fans earn most</div>
            <div className="vp-card-desc">First fans on a rising artist earn more than anyone who joins later.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">👥</span>
            <div className="vp-card-title">Fans invested in you</div>
            <div className="vp-card-desc">Your street team earns SOUND supporting you. They want you to win.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">🎵</span>
            <div className="vp-card-title">Support art you love</div>
            <div className="vp-card-desc">Win or lose, you&#39;re backing artists who deserve it.</div>
          </div>
        </div>

        <div className="vp-divider" />

        {/* ── Closing CTA ────────────────────────────────────────────────── */}
        <div className="pl-cta-row pl-cta-row--closing">
          <Link className="pl-cta-primary" to="/creators">
            Browse the roster →
          </Link>
          <Link className="pl-cta-secondary" to="/login">
            Create your account
          </Link>
        </div>
      </div>

      <footer className="pl-footer">
        <span>SoundDAO · Gamifying Fandom</span>
        <span> · <Link to="/legal/terms">Terms</Link> · <Link to="/legal/privacy">Privacy</Link> · <Link to="/legal/copyright">Copyright &amp; DMCA</Link></span>
      </footer>
    </div>
  );
}
